/**
 * @file Drop entry for the Home and Forms-list pages. For an intake reviewer
 * (`aiOperator` or `aiAdmin`) a dropped or browsed PDF goes straight to PDF
 * intake (`startPdfIntake`) with a new draft form of the default type, and the
 * page opens its schedule review at `/intake/schedules/<sha>`; the agent
 * sidebar stays closed. Every other file, and a PDF from anyone else, is handed
 * to the agent (`AIContext.startDocumentImportRun`) as before. The default form
 * type is resolved from `parameters/aiEmailAnalysis` by
 * `resolveDefaultFormType`. Nothing store-derived reaches render, so the hook
 * holds no `useSnapshot`: it `connect()`s the parameters document and
 * `handleDrop` reads it off the raw proxy at drop time.
 */
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AGENT_EMAIL_EXTENSIONS, AGENT_TABULAR_EXTENSIONS } from '#types';
import type { AiEmailAnalysisParameters } from '#types';
import { UserContext } from '../providers/UserContext';
import { AIContext } from '../providers/AIContext';
import { MessageContext } from '../providers/MessageContext';
import { startPdfIntake } from '../services/startPdfIntake';
import { resolveDefaultFormType } from '../services/resolveDefaultFormType';
import { createDocumentStore } from '../store';

const aiEmailParamsStore = createDocumentStore<AiEmailAnalysisParameters>(
  'parameters/aiEmailAnalysis',
);

const VALID_DROP_EXTENSIONS = [
  ...AGENT_EMAIL_EXTENSIONS,
  ...AGENT_TABULAR_EXTENSIONS,
].map((ext) => `.${ext}`);

const useFileDrop = () => {
  const { user, userRights } = useContext(UserContext);
  const { startDocumentImportRun } = useContext(AIContext);
  const { messageApi } = useContext(MessageContext);
  const navigate = useNavigate();

  const dropDisabled = !userRights.aiAgent;
  const pdfsOpenInIntake = !!(userRights.aiOperator || userRights.aiAdmin);

  useEffect(() => {
    if (dropDisabled) return;
    aiEmailParamsStore.connect();
    return () => {
      aiEmailParamsStore.disconnect();
    };
  }, [dropDisabled]);

  const handleDrop = async (file: File) => {
    const defaultFormType = resolveDefaultFormType(
      aiEmailParamsStore.state.data ?? undefined,
    );
    if (pdfsOpenInIntake && user && file.name.toLowerCase().endsWith('.pdf')) {
      const jobId = await startPdfIntake({
        file,
        order: { newForm: defaultFormType },
        userId: user.uid,
        messageApi,
      });
      if (jobId) navigate(`/intake/schedules/${jobId}`);
      return;
    }
    await startDocumentImportRun([file], defaultFormType);
  };

  return {
    dropDisabled,
    pdfsOpenInIntake,
    validDropExtensions: VALID_DROP_EXTENSIONS,
    handleDrop,
    handleFileBrowse: handleDrop,
  };
};

export default useFileDrop;
